import { Button } from "@chakra-ui/react";
import { keepPreviousData } from "@tanstack/react-query";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import {
  useAdminCustomerEventImpactingEventsOptionsRetrieve,
  useAdminCustomerEventImpactingEventsTableDataRetrieve,
  useAdminCustomerEventTransactionTableDataRetrieve,
  useCustomerEventRetrieveSuspense,
} from "gen/endpoints/customer-event/customer-event";
import { compact, isEmpty } from "lodash-es";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectButton } from "shared/components/input/select/SelectButton";
import { selectOptionsFromGenericSelectData } from "shared/components/input/select/SelectUtils";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { StringArrayParam } from "shared/utils/QueryParamUtils";
import { useQueryParams } from "use-query-params";

export const EventPage: React.FC = () => {
  const { t } = useTranslation(["common", "admin", "capitalActivity", "enums"]);
  const { customerEventId } = useRequiredStringParams();

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data: customerEvent } =
    useCustomerEventRetrieveSuspense(customerEventId);

  const { data: impactingEventOptionsData } =
    useAdminCustomerEventImpactingEventsOptionsRetrieve(customerEventId, {
      query: {
        enabled: !!customerEvent.fund_family_event_id,
      },
    });

  const [filters, setFilters] = useQueryParams({
    fund_family_event_ids: StringArrayParam,
  });

  const hasFilters = Object.values(filters).some((value) => !isEmpty(value));
  const defaultFundFamilyEventId = compact([
    customerEvent.fund_family_event_id,
  ]);

  const clearFilters = () =>
    setFilters({
      fund_family_event_ids: defaultFundFamilyEventId,
    });

  const setIsEqualizedViewOnChange = (isEqualizedView: boolean) => {
    setIsEqualizedView(isEqualizedView);
    setFilters({
      fund_family_event_ids: isEqualizedView
        ? impactingEventOptionsData?.map((eventOptions) => eventOptions.value)
        : defaultFundFamilyEventId,
    });
  };

  const { data: impactingEventTableData } =
    useAdminCustomerEventImpactingEventsTableDataRetrieve(
      customerEventId,
      {
        is_equalized_view: isEqualizedView,
      },
      {
        query: {
          enabled: !!customerEvent.fund_family_event_id,
          placeholderData: keepPreviousData,
        },
      },
    );

  const { data: transactionTableData } =
    useAdminCustomerEventTransactionTableDataRetrieve(
      customerEventId,
      {
        posted_fund_family_event_ids:
          filters.fund_family_event_ids || defaultFundFamilyEventId,
      },
      {
        query: {
          enabled: !!customerEvent.fund_family_event_id,
          placeholderData: keepPreviousData,
        },
      },
    );

  const getLinkInfoForEventRow = useCallback<GetLinkInfoForRowFn>(({ row }) => {
    const metadata = row.data?.metadata;
    if (metadata?.type === "id_row") {
      return {
        type: "generic",
        to: rootRoute({}).admin({}).events({}).event({
          customerEventId: metadata.id,
        }).$,
      };
    }
    return undefined;
  }, []);

  return (
    <PageContent>
      <PageHeader
        title={
          customerEvent.subtype
            ? `${customerEvent.type} - ${customerEvent.subtype}`
            : customerEvent.type
        }
        actions={
          <EqualizedViewToggleButton
            isEqualizedView={isEqualizedView}
            setIsEqualizedView={setIsEqualizedViewOnChange}
            size="lg"
          />
        }
        helperText={
          customerEvent.effective_date && customerEvent.posted_date
            ? t("admin:adminEventPage.helperText", {
                effective_date: formatIsoDateToDisplay(
                  customerEvent.effective_date,
                ),
                posted_date: formatIsoDateToDisplay(customerEvent.posted_date),
              })
            : undefined
        }
        badge={
          customerEvent.state
            ? getCapitalActivityEventStateBadge(customerEvent.state, t)
            : undefined
        }
        backButton={{
          title: t("common:back"),
          path: rootRoute({}).admin({}).events({}).$,
        }}
      />
      <PageBody>
        {!!impactingEventTableData?.rows.length && (
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>{t("common:events")}</TableBoxTitle>
              <DataTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable
                data={impactingEventTableData}
                getLinkInfoForRow={getLinkInfoForEventRow}
              />
            </TableBoxBody>
          </TableBox>
        )}
        {!!transactionTableData?.rows.length && (
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>{t("common:transactions")}</TableBoxTitle>
              <DataTableBoxActions>
                {impactingEventOptionsData?.length && (
                  <SelectButton
                    isMulti={true}
                    title={t("common:events")}
                    size="sm"
                    isFilterButton={true}
                    value={
                      filters.fund_family_event_ids || defaultFundFamilyEventId
                    }
                    onChangeValue={(fund_family_event_ids) =>
                      setFilters({
                        fund_family_event_ids: isEmpty(fund_family_event_ids)
                          ? defaultFundFamilyEventId
                          : fund_family_event_ids,
                      })
                    }
                  >
                    {selectOptionsFromGenericSelectData(
                      impactingEventOptionsData,
                    )}
                  </SelectButton>
                )}
                {hasFilters && (
                  <Button variant="subtle" onClick={clearFilters}>
                    {t("common:reset")}
                  </Button>
                )}
              </DataTableBoxActions>
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable data={transactionTableData} />
            </TableBoxBody>
          </TableBox>
        )}
      </PageBody>
    </PageContent>
  );
};
